import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HoaDonMuaHangTrongNuocSearchProvider, useHoaDonMuaHangTrongNuocMutation } from './context';
import { ConfirmationDialog, LoadingOverlayV2 } from '@/components/custom/erp';
import FormDialog from './components/form-dialog';
import { QUERY_KEYS } from '@/constants';
import { useCRUD } from '@/hooks';

type FormMode = 'add' | 'edit' | 'view';

interface DocumentEntryProps {
  document_uuid?: string;
  document_type?: string;
  formMode?: FormMode;
  initialData?: MuaHangRecord;
  onClose?: () => void;
  onSubmit?: (data: MuaHangRecord) => void | Promise<void>;
  onMutated?: () => void | Promise<void>;
}

function DocumentEntryContent({
  document_uuid,
  formMode: initialFormMode = 'view',
  initialData,
  onClose,
  onSubmit,
  onMutated
}: DocumentEntryProps) {
  const [formMode, setFormMode] = useState<FormMode>(initialFormMode);
  const [isCopyMode, setIsCopyMode] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [entryDetail, setEntryDetail] = useState<MuaHangRecord>(initialData);
  const [loadedDocumentUuid, setLoadedDocumentUuid] = useState<string | null>(null);
  const isDeletingRef = useRef(false);
  const latestSavedDetailRef = useRef<MuaHangRecord>(initialData);
  const lastFetchedUuidRef = useRef<string | null>(null);
  const { fetchDetail, addItem, updateItem, deleteItem } = useCRUD({
    endpoint: QUERY_KEYS.HOA_DON_MUA_HANG_TRONG_NUOC,
    initialFetch: false
  });

  useEffect(() => {
    setEntryDetail(initialData);
    latestSavedDetailRef.current = initialData;
    setFormMode(initialFormMode);
    setIsCopyMode(false);
    lastFetchedUuidRef.current = null;
    setLoadedDocumentUuid(null);
  }, [document_uuid, initialData, initialFormMode]);

  const selectedObj = entryDetail ?? initialData;
  const formInitialData = formMode === 'add' && !isCopyMode ? undefined : selectedObj;
  const isDocumentDetailPending = Boolean(
    document_uuid &&
    formMode !== 'add' &&
    !isCopyMode &&
    (!selectedObj || selectedObj?.uuid === document_uuid) &&
    loadedDocumentUuid !== document_uuid
  );

  const handleDialogClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleMutationCloseForm = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleRefreshData = useCallback(async () => {
    await onMutated?.();
    await onSubmit?.(latestSavedDetailRef.current);
  }, [onMutated, onSubmit]);

  const handleFetchDetail = useCallback(
    async (uuid: string) => {
      const detail = await fetchDetail(uuid);
      setEntryDetail(detail);
      latestSavedDetailRef.current = detail;
      setLoadedDocumentUuid(uuid);
      return detail;
    },
    [fetchDetail]
  );

  useEffect(() => {
    if (!document_uuid || formMode === 'add' || isCopyMode || lastFetchedUuidRef.current === document_uuid) return;

    lastFetchedUuidRef.current = document_uuid;
    void handleFetchDetail(document_uuid);
  }, [document_uuid, formMode, handleFetchDetail, isCopyMode]);

  const handleAddItem = useCallback(
    async (data: MuaHangRecord) => {
      const created = await addItem(data);
      setEntryDetail(created);
      latestSavedDetailRef.current = created;
      return created;
    },
    [addItem]
  );

  const handleUpdateItem = useCallback(
    async (uuid: string, data: MuaHangRecord) => {
      const updated = await updateItem(uuid, data);
      setEntryDetail(updated);
      latestSavedDetailRef.current = updated;
      return updated;
    },
    [updateItem]
  );

  const handleDeleteItem = useCallback(
    async (uuid: string | undefined) => {
      if (!uuid) return;
      await deleteItem(uuid);
      setEntryDetail(null);
      latestSavedDetailRef.current = null;
    },
    [deleteItem]
  );

  const tableState = useMemo(
    () => ({
      crud: {
        fetchDetail: handleFetchDetail,
        addItem: handleAddItem,
        updateItem: handleUpdateItem,
        deleteItem: handleDeleteItem,
        refreshData: handleRefreshData
      },
      selection: {
        selectedObj,
        clearSelection: () => undefined
      },
      form: {
        formMode,
        handleCloseForm: handleMutationCloseForm,
        handleCloseDelete: () => setShowDelete(false),
        handleAddClick: () => {
          setIsCopyMode(false);
          setFormMode('add');
        },
        handleEditClick: () => {
          setIsCopyMode(false);
          setFormMode('edit');
        },
        handleDeleteClick: () => setShowDelete(true),
        handleCopyClick: () => {
          setIsCopyMode(true);
          setFormMode('add');
        }
      }
    }),
    [
      formMode,
      handleAddItem,
      handleDeleteItem,
      handleFetchDetail,
      handleMutationCloseForm,
      handleRefreshData,
      handleUpdateItem,
      selectedObj
    ]
  );

  const mutationState = useHoaDonMuaHangTrongNuocMutation({ tableState });

  const handleAdd = useCallback(() => {
    setIsCopyMode(false);
    setFormMode('add');
  }, []);

  const handleCopy = useCallback(() => {
    setIsCopyMode(true);
    setFormMode('add');
  }, []);

  const handleDeleteClick = useCallback(() => {
    setShowDelete(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    isDeletingRef.current = true;
    try {
      await mutationState.handleDelete();
    } finally {
      isDeletingRef.current = false;
    }
  }, [mutationState]);

  if (isDocumentDetailPending) {
    return (
      <div className='relative h-full min-h-0 w-full'>
        <LoadingOverlayV2 message='Đang tải...' />
      </div>
    );
  }

  return (
    <>
      <FormDialog
        open
        formMode={formMode}
        initialData={formInitialData}
        onClose={handleDialogClose}
        onSubmit={mutationState.handleSubmit}
        onAdd={handleAdd}
        onEdit={mutationState.handleEditClick}
        onDelete={handleDeleteClick}
        onCopy={handleCopy}
      />

      <ConfirmationDialog
        open={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={handleConfirmDelete}
        selectedObj={selectedObj}
        title='Xóa hóa đơn mua hàng'
        message='Bạn có chắc chắn muốn xóa hóa đơn mua hàng này không?'
        confirmButtonText='Xóa'
        cancelButtonText='Hủy'
      />
    </>
  );
}

export default function DocumentEntry(props: DocumentEntryProps) {
  return (
    <HoaDonMuaHangTrongNuocSearchProvider>
      <DocumentEntryContent {...props} />
    </HoaDonMuaHangTrongNuocSearchProvider>
  );
}
